import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Save, Loader2, Building2, MapPin, Globe2, Package, DollarSign, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';

// Options for dropdowns
const INDUSTRIES = [
  'Pet Supplies',
  'Retail',
  'E-commerce',
  'Manufacturing',
  'Food & Beverage',
  'Fashion',
  'Electronics',
  'Health & Beauty',
  'Other'
];

const COMPANY_SIZES = [
  '1-10 employees',
  '11-50 employees',
  '51-200 employees',
  '201-500 employees',
  '500+ employees'
];

const TIMEZONES = [
  'America/Detroit',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'America/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Australia/Sydney'
];

const CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' }
];

const DATE_FORMATS = [
  'MM/DD/YYYY',
  'DD/MM/YYYY',
  'YYYY-MM-DD'
];

const NUMBER_FORMATS = [
  '1,234.56',
  '1.234,56',
  '1 234,56'
];

const WEIGHT_UNITS = ['LB', 'KG', 'OZ'];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const CompanySettings = () => {
  const { user, role, tenantId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  
  const isOwnerOrAdmin = role === 'Owner' || role === 'Admin';
  
  // Form state
  const [formData, setFormData] = useState({
    company_name: '',
    logo_url: null,
    industry: null,
    company_size: null,
    website: '',
    description: '',
    tax_id: '',
    business_registration_number: '',
    business_address: {
      street_line1: '',
      street_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'United States'
    },
    business_phone: '',
    business_email: '',
    timezone: 'America/Detroit',
    currency: 'USD',
    date_format: 'MM/DD/YYYY',
    number_format: '1,234.56',
    weight_unit: 'LB',
    default_warehouse_id: null,
    default_reorder_point: 20,
    default_target_stock: 70,
    fiscal_year_start: 'January',
    default_tax_rate: 0
  });
  
  const [originalData, setOriginalData] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchWarehouses();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/v1/company/settings', {
        headers: { 'X-Tenant-ID': tenantId || 'tenant-123' }
      });
      
      if (response.success) {
        const settings = response.settings;
        setFormData(settings);
        setOriginalData(JSON.stringify(settings));
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
      toast.error('Failed to load company settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const response = await api.get('/api/v1/company/warehouses', {
        headers: { 'X-Tenant-ID': tenantId || 'tenant-123' }
      });
      
      if (response.success) {
        setWarehouses(response.warehouses);
      }
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleAddressChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      business_address: {
        ...prev.business_address,
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!isOwnerOrAdmin) {
      toast.error('Only Owner and Admin can modify company settings');
      return;
    }

    // Validation
    if (!formData.company_name || formData.company_name.trim().length < 2) {
      toast.error('Company name must be at least 2 characters');
      return;
    }

    try {
      setSaving(true);
      
      const response = await api.put('/api/v1/company/settings', formData, {
        headers: { 'X-Tenant-ID': tenantId || 'tenant-123' }
      });

      if (response.data.success) {
        toast.success('Company settings saved successfully');
        setOriginalData(JSON.stringify(formData));
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Error saving company settings:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to save company settings';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (originalData) {
      setFormData(JSON.parse(originalData));
      setHasChanges(false);
      toast.info('Changes discarded');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }
    
    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error('Only PNG, JPG, and SVG files are allowed');
      return;
    }
    
    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      handleChange('logo_url', reader.result);
      toast.success('Logo uploaded successfully');
    };
    reader.onerror = () => {
      toast.error('Failed to read file');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    handleChange('logo_url', null);
    toast.info('Logo removed');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Company Settings</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage your company profile and default settings
          </p>
        </div>
        
        {isOwnerOrAdmin && (
          <div className="flex gap-2">
            {hasChanges && (
              <Button onClick={handleCancel} variant="outline" disabled={saving}>
                Cancel
              </Button>
            )}
            <Button 
              onClick={handleSave} 
              disabled={!hasChanges || saving}
              className="gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {!isOwnerOrAdmin && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-900 dark:text-amber-100">Read-only access</p>
            <p className="text-sm text-amber-700 dark:text-amber-200 mt-1">
              Only Owners and Admins can modify company settings. Contact your administrator to make changes.
            </p>
          </div>
        </div>
      )}

      {/* Company Profile Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            <CardTitle>Company Profile</CardTitle>
          </div>
          <CardDescription>Basic information about your company</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.company_name || ''}
                onChange={(e) => handleChange('company_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                placeholder="Your Company Name"
                disabled={!isOwnerOrAdmin}
                required
              />
            </div>

            {/* Company Logo */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Company Logo
              </label>
              <div className="flex items-center gap-4">
                {/* Logo Preview */}
                <div className="w-20 h-20 rounded-lg bg-gray-100 dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 flex items-center justify-center overflow-hidden">
                  {formData.logo_url ? (
                    <img 
                      src={formData.logo_url} 
                      alt="Company logo" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-2xl font-bold text-gray-400 dark:text-gray-500">
                      {formData.company_name?.charAt(0)?.toUpperCase() || 'C'}
                    </span>
                  )}
                </div>
                
                {/* Upload Controls */}
                <div>
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                    onChange={handleLogoUpload}
                    className="hidden"
                    disabled={!isOwnerOrAdmin}
                  />
                  <label
                    htmlFor="logo-upload"
                    className={`inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors ${
                      !isOwnerOrAdmin ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                    }`}
                  >
                    {formData.logo_url ? 'Change Logo' : 'Upload Logo'}
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    PNG, JPG or SVG (max 2MB)
                  </p>
                  {formData.logo_url && isOwnerOrAdmin && (
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 mt-1 block"
                    >
                      Remove Logo
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Industry
              </label>
              <select
                value={formData.industry || ''}
                onChange={(e) => handleChange('industry', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                disabled={!isOwnerOrAdmin}
              >
                <option value="">Select Industry</option>
                {INDUSTRIES.map(industry => (
                  <option key={industry} value={industry}>{industry}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Company Size
              </label>
              <select
                value={formData.company_size || ''}
                onChange={(e) => handleChange('company_size', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                disabled={!isOwnerOrAdmin}
              >
                <option value="">Select Company Size</option>
                {COMPANY_SIZES.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Website
              </label>
              <input
                type="url"
                value={formData.website || ''}
                onChange={(e) => handleChange('website', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                placeholder="https://www.example.com"
                disabled={!isOwnerOrAdmin}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Business Phone
              </label>
              <input
                type="tel"
                value={formData.business_phone || ''}
                onChange={(e) => handleChange('business_phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                placeholder="+1 (XXX) XXX-XXXX"
                disabled={!isOwnerOrAdmin}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Company Description
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                placeholder="Brief description of your business..."
                rows={3}
                maxLength={500}
                disabled={!isOwnerOrAdmin}
              />
              <p className="text-xs text-gray-500 mt-1">
                {(formData.description || '').length} / 500 characters
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Information Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-green-600" />
            <CardTitle>Business Information</CardTitle>
          </div>
          <CardDescription>Legal and contact information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tax ID / EIN
              </label>
              <input
                type="text"
                value={formData.tax_id || ''}
                onChange={(e) => handleChange('tax_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                placeholder="XX-XXXXXXX"
                disabled={!isOwnerOrAdmin}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Business Email
              </label>
              <input
                type="email"
                value={formData.business_email || ''}
                onChange={(e) => handleChange('business_email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                placeholder="contact@company.com"
                disabled={!isOwnerOrAdmin}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Business Address
              </label>
              <div className="space-y-3">
                <input
                  type="text"
                  value={formData.business_address?.street_line1 || ''}
                  onChange={(e) => handleAddressChange('street_line1', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  placeholder="Street Address Line 1"
                  disabled={!isOwnerOrAdmin}
                />
                <input
                  type="text"
                  value={formData.business_address?.street_line2 || ''}
                  onChange={(e) => handleAddressChange('street_line2', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  placeholder="Street Address Line 2 (optional)"
                  disabled={!isOwnerOrAdmin}
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    value={formData.business_address?.city || ''}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    placeholder="City"
                    disabled={!isOwnerOrAdmin}
                  />
                  <input
                    type="text"
                    value={formData.business_address?.state || ''}
                    onChange={(e) => handleAddressChange('state', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    placeholder="State"
                    disabled={!isOwnerOrAdmin}
                  />
                  <input
                    type="text"
                    value={formData.business_address?.postal_code || ''}
                    onChange={(e) => handleAddressChange('postal_code', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    placeholder="Postal Code"
                    disabled={!isOwnerOrAdmin}
                  />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Regional Settings Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Globe2 className="h-5 w-5 text-purple-600" />
            <CardTitle>Regional Settings</CardTitle>
          </div>
          <CardDescription>Configure timezone, currency, and formatting preferences</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Timezone <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.timezone || 'America/Detroit'}
                onChange={(e) => handleChange('timezone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                disabled={!isOwnerOrAdmin}
              >
                {TIMEZONES.map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Currency <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.currency || 'USD'}
                onChange={(e) => handleChange('currency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                disabled={!isOwnerOrAdmin}
              >
                {CURRENCIES.map(curr => (
                  <option key={curr.code} value={curr.code}>
                    {curr.code} - {curr.name} ({curr.symbol})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date Format
              </label>
              <select
                value={formData.date_format || 'MM/DD/YYYY'}
                onChange={(e) => handleChange('date_format', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                disabled={!isOwnerOrAdmin}
              >
                {DATE_FORMATS.map(format => (
                  <option key={format} value={format}>{format}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Number Format
              </label>
              <select
                value={formData.number_format || '1,234.56'}
                onChange={(e) => handleChange('number_format', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                disabled={!isOwnerOrAdmin}
              >
                {NUMBER_FORMATS.map(format => (
                  <option key={format} value={format}>{format}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Weight Unit
              </label>
              <div className="flex gap-4">
                {WEIGHT_UNITS.map(unit => (
                  <label key={unit} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value={unit}
                      checked={formData.weight_unit === unit}
                      onChange={(e) => handleChange('weight_unit', e.target.value)}
                      className="w-4 h-4 text-blue-600"
                      disabled={!isOwnerOrAdmin}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{unit}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Default Warehouse Settings Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-orange-600" />
            <CardTitle>Default Warehouse Settings</CardTitle>
          </div>
          <CardDescription>Default values for new products</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Default Warehouse
              </label>
              <select
                value={formData.default_warehouse_id || ''}
                onChange={(e) => handleChange('default_warehouse_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                disabled={!isOwnerOrAdmin}
              >
                <option value="">Select Warehouse</option>
                {warehouses.map(wh => (
                  <option key={wh.id} value={wh.id}>{wh.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Default Reorder Point
              </label>
              <input
                type="number"
                value={formData.default_reorder_point || 20}
                onChange={(e) => handleChange('default_reorder_point', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                placeholder="20"
                disabled={!isOwnerOrAdmin}
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Default Target Stock
              </label>
              <input
                type="number"
                value={formData.default_target_stock || 70}
                onChange={(e) => handleChange('default_target_stock', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                placeholder="70"
                disabled={!isOwnerOrAdmin}
                min="0"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fiscal Settings Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-indigo-600" />
            <CardTitle>Fiscal Settings</CardTitle>
          </div>
          <CardDescription>Configure fiscal year and tax settings</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Fiscal Year Start
              </label>
              <select
                value={formData.fiscal_year_start || 'January'}
                onChange={(e) => handleChange('fiscal_year_start', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                disabled={!isOwnerOrAdmin}
              >
                {MONTHS.map(month => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Default Tax Rate (%)
              </label>
              <input
                type="number"
                value={formData.default_tax_rate || 0}
                onChange={(e) => handleChange('default_tax_rate', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                placeholder="8.5"
                disabled={!isOwnerOrAdmin}
                min="0"
                max="100"
                step="0.01"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompanySettings;